import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Enhanced type safety for chat channel
interface ChatChannel {
  url: string;
  name?: string;
  memberCount?: number;
  unreadMessageCount?: number;
  lastMessage?: {
    message: string;
    createdAt: number;
    sender?: {
      userId: string;
      nickname: string;
    };
  };
}

interface ChatState {
  totalUnreadCount: number;
  currentChannelUrl: string | null;
  currentChannel: ChatChannel | null;
  showChannel: boolean;
  channelUnreadCounts: Record<string, number>;
  isConnected: boolean;
  lastUpdated: number;
  initializationStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: ChatState = {
  totalUnreadCount: 0,
  currentChannelUrl: null,
  currentChannel: null,
  showChannel: false,
  channelUnreadCounts: {},
  isConnected: false,
  lastUpdated: Date.now(),
  initializationStatus: 'idle',
  error: null,
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setTotalUnreadCount: (state, action: PayloadAction<number>) => {
      state.totalUnreadCount = action.payload;
      state.lastUpdated = Date.now();
    },
    setChannelUnreadCount: (
      state,
      action: PayloadAction<{ channelUrl: string; count: number }>,
    ) => {
      state.channelUnreadCounts[action.payload.channelUrl] =
        action.payload.count;
      state.lastUpdated = Date.now();
    },
    setCurrentChannelUrl: (state, action: PayloadAction<string | null>) => {
      state.currentChannelUrl = action.payload;
    },
    setConnectionStatus: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
    },
    clearChannelUnreadCount: (state, action: PayloadAction<string>) => {
      if (state.channelUnreadCounts[action.payload]) {
        const channelCount = state.channelUnreadCounts[action.payload];
        state.totalUnreadCount = Math.max(
          0,
          state.totalUnreadCount - channelCount,
        );
        state.channelUnreadCounts[action.payload] = 0;
        state.lastUpdated = Date.now();
      }
    },
    setCurrentChannel: (state, action: PayloadAction<ChatChannel | null>) => {
      state.currentChannel = action.payload;
    },
    setShowChannel: (state, action: PayloadAction<boolean>) => {
      state.showChannel = action.payload;
    },
    clearCurrentChannel: (state) => {
      state.currentChannelUrl = null;
      state.currentChannel = null;
    },
    setInitializationStatus: (state, action: PayloadAction<ChatState['initializationStatus']>) => {
      state.initializationStatus = action.payload;
    },
    setChatError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    resetChatState: (state) => {
      return {
        ...initialState,
        lastUpdated: Date.now(),
      };
    },
    restoreSessionChatState: (
      state,
      action: PayloadAction<{
        currentChannelUrl: string;
        currentChannel: ChatChannel;
      }>
    ) => {
      state.currentChannelUrl = action.payload.currentChannelUrl;
      state.currentChannel = action.payload.currentChannel;
      state.showChannel = false; // Start with chat closed, will be opened by user
    },
  },
});

export const {
  setTotalUnreadCount,
  setChannelUnreadCount,
  setCurrentChannelUrl,
  setConnectionStatus,
  clearChannelUnreadCount,
  setCurrentChannel,
  setShowChannel,
  clearCurrentChannel,
  setInitializationStatus,
  setChatError,
  resetChatState,
  restoreSessionChatState,
} = chatSlice.actions;

// Export types for use in other files
export type { ChatChannel, ChatState };

export default chatSlice.reducer;
